import type { FontWeight, GTextProps } from '@shared-ui/components/ui/text/types'
import { getSettings } from '@shared-ui/theme'
import { typography } from '@shared-ui/theme/foundations'
import { MAX_SYSTEM_FONT_SCALE, MIN_SYSTEM_FONT_SCALE, rf } from '@shared-ui/theme/scale'
import { type ComponentRef, type ForwardedRef, forwardRef } from 'react'
import { Platform, StyleSheet, Text, type TextProps, type TextStyle } from 'react-native'
import Animated from 'react-native-reanimated'
import { useCreateStyles } from './styles'

const { APP_DEFAULT_FONT_FAMILY } = getSettings()

type CalculateFontSizeByLengthProps = {
  maxLen: number
  minSize?: number
  size?: number
  style?: TextProps['style']
}

// расчет размера текста в зависимости от длины строки
export const calculateFontSizeByLength = (
  value: string,
  { minSize, maxLen, size: _size }: CalculateFontSizeByLengthProps,
) => {
  const len = String(value).trim().length
  const defaultSize = _size || 16
  const proportion = maxLen / len
  const size = defaultSize * Math.min(proportion, 1)

  return rf(Math.ceil(Math.max(size, minSize || 0)))
}

const monospaceFontFamily = Platform.OS === 'ios' ? 'Courier' : 'monospace'
const androidFamilyAdditional = {
  '900': 'Black',
  '800': 'ExtraBold',
  '700': 'Bold',
  '600': 'SemiBold',
  '500': 'Medium',
  '400': 'Regular',
  '300': 'Light',
  '200': 'ExtraLight',
  '100': 'ExtraLight',
  normal: 'Regular',
  bold: 'Bold',
}
export const GText = forwardRef(function GTextComponent(
  props: GTextProps,
  ref: ForwardedRef<ComponentRef<typeof Text>>,
) {
  const {
    style,
    variant,
    size,
    color,
    fw,
    uppercase,
    isSecondary,
    align,
    lh,
    isPrimary,
    isDanger,
    isWarning,
    isSuccess,
    ...otherProps
  } = props
  const styles = useCreateStyles()
  const token = variant ? typography[variant] : undefined
  const localStyle: TextProps['style'] = token
    ? {
        fontSize: rf(token.fontSize),
        lineHeight: rf(token.lineHeight),
        fontWeight: token.fontWeight,
        letterSpacing: rf(token.letterSpacing),
      }
    : {}

  if (size) {
    Object.assign(localStyle, { fontSize: rf(size) })
  }

  if (size === null) {
    Object.assign(localStyle, { fontSize: undefined })
  }

  if (fw) {
    Object.assign(localStyle, { fontWeight: fw })
  }

  if (uppercase) {
    Object.assign(localStyle, { textTransform: 'uppercase' })
  }

  if (color) {
    Object.assign(localStyle, { color: color })
  }

  if (lh) {
    Object.assign(localStyle, { lineHeight: rf(lh) })
  }

  if (align) {
    Object.assign(localStyle, { textAlign: align })
  }

  const flattened = StyleSheet.flatten([
    styles.defaultText,
    localStyle,
    style,
    isSecondary && styles.secondaryColor,
    isPrimary && styles.primaryColor,
    isDanger && styles.dangerColor,
    isWarning && styles.warningColor,
    isSuccess && styles.successColor,
  ] as never)
  const finishStyle = { ...(flattened as TextStyle) } as TextStyle & {
    fontFamily?: string
    fontWeight?: TextStyle['fontWeight']
  }
  const fontFamilyType = androidFamilyAdditional[(finishStyle.fontWeight as '100') || '400']
  finishStyle.fontFamily = `${APP_DEFAULT_FONT_FAMILY}-${fontFamilyType}`

  if (!props.isMonospace) {
    if (Platform.OS === 'android') {
      finishStyle.fontWeight = 'normal'
    }
  } else {
    finishStyle.fontFamily = monospaceFontFamily
  }

  return (
    <Text
      ref={ref}
      {...otherProps}
      maxFontSizeMultiplier={MAX_SYSTEM_FONT_SCALE}
      minimumFontScale={MIN_SYSTEM_FONT_SCALE}
      style={finishStyle}
    />
  )
})

export const GTextAnimated = Animated.createAnimatedComponent(GText)

type GTitleProps = GTextProps & {
  level?: 'H1' | 'H2'
}

const titleLevelConfig: Record<'H1' | 'H2', { size: number; fw: FontWeight; styleKey: 'h1' | 'h2' }> = {
  H1: { size: 24, fw: 600, styleKey: 'h1' },
  H2: { size: 18, fw: 600, styleKey: 'h2' },
}

export function GTitle({ style, level = 'H1', ...props }: GTitleProps) {
  const styles = useCreateStyles()
  const { size, fw, styleKey } = titleLevelConfig[level]

  return <GText size={size} fw={fw} style={StyleSheet.flatten([styles[styleKey], style])} {...props} />
}
